import { i18n } from '@/i18n';
import { useCouponStore } from '@/store/coupon';
import { CustomError, to } from '@/utils/to';
import { elk } from '@tencent/ppd-base-libs';
import dayjs from 'dayjs';
import { ExActAndConfigCouponInfo } from '../types/coupon';
import { getServerTime } from '../utils/date-helper';
import { AsyncExchangePrizeResult } from '@tencent/wphk-act-base-cgi';
import { createEventHook } from '@vueuse/core';
import { onUnmounted } from '@vue/composition-api';
import { isInterruptError } from '@/utils/interrupt';

export interface ExchErrorType extends CustomError  {
  errorType: ExchError;
};
/** 返回兑换错误的Error对象 */
function getExchError(errorType: ExchError, error?: CustomError) {
  return { ...error, errorType };
}

/** 兑换错误枚举 */
export enum ExchError {
  /** 兑换错误-已兑完 */
  EXCH_OUT_OF_STOCK = 'EXCH_OUT_OF_STOCK',
  /** 兑换错误-其他错误*/
  EXCH_OTHER_ERROR = 'EXCH_OTHER_ERROR',
  /** 兑换错误-兑换终止*/
  EXCH_INTERRUPT_ERROR = 'EXCH_INTERRUPT_ERROR',
  /** 刷新错误 - 兑换失败 */
  REFRESH_ERROR = 'REFRESH_ERROR',
  /** 刷新错误 - 兑换成功 */
  REFRESH_ERROR_EXCHANGED = 'REFRESH_ERROR_EXCHANGED'
}

/** 兑换错误文案 */
export const ExchErrorText = {
  [ExchError.EXCH_OUT_OF_STOCK]: i18n('搶曬啦'),
  [ExchError.EXCH_OTHER_ERROR]: i18n('兌換失败'),
  [ExchError.REFRESH_ERROR]: i18n('頁面刷新失敗，稍後再試'),
  [ExchError.REFRESH_ERROR_EXCHANGED]: i18n('兑换成功后,数据reload失败'),
};

/** 处理券兑换流程 */
export const useExchange = () => {
  const couponStore = useCouponStore();
  const interruptExchange = createEventHook(); // 中断兑换流程
  /**
 *
 * @param couponInfo
 * @return
 */
  const handleExchPrize = async (couponInfo: ExActAndConfigCouponInfo) => {
    if (couponInfo) {
      const { enExActId, enCouponId, exQualId } = couponInfo;
      // 上报用户兑换时的时间等信息
      reportExchAction({ couponInfo, action: 'beforeExchange' });
      const [exchangeErr, exchangeResult] = await to(couponStore.handleExchangePrize({
        qualId: exQualId,
        actListid: enExActId,
        prizeNo: enCouponId,
      }, interruptExchange.on));
      reportExchAction({ action: 'endExchange' });
      if (exchangeErr || exchangeResult !== AsyncExchangePrizeResult.success) {
        try {
          elk({
            key: 'showErrorDialog',
            str2: 'exchangeErrorToast',
            str3: exchangeErr?.retmsg || exchangeErr?.message || exchangeResult,
            msg: JSON.stringify(exchangeErr),
          });
        } catch (e) {}
        if (exchangeResult === AsyncExchangePrizeResult.limtFail) { // 限额报错，提示库存不足
          throw getExchError(ExchError.EXCH_OUT_OF_STOCK);
        } else if (exchangeErr && isInterruptError(exchangeErr)) { // 兑换流程中断(页面切换等导致)，无提示
          throw getExchError(ExchError.EXCH_INTERRUPT_ERROR);
        } else {
          throw getExchError(ExchError.EXCH_OTHER_ERROR, exchangeErr || undefined);
        }
      }
    }
  };
  onUnmounted(() => {
    interruptExchange.trigger(true);
  });
  return { handleExchPrize };
};

// 兑换动作的时间上报 - 用于定位12点前兑换券的问题
export const reportExchAction = (params: { couponInfo?: ExActAndConfigCouponInfo, action?: string }) => {
  const { couponInfo, action } = params;
  const formatTime = (time: Date) => dayjs(time).format('YYYY/MM/DD HH:mm:ss:SSS');
  const windowTime = (window as any).__$$_serverTimeMills;
  elk({
    key: 'exchCouponTimeStamp',
    str1: `client-time: ${formatTime(new Date())}`,
    str2: `wphk-components-common-time: ${formatTime(getServerTime())}`,
    str3: `apache-time: ${windowTime ? formatTime(new Date(windowTime)) : 'undefined'}`,
    str4: action,
    msg: JSON.stringify(couponInfo),
  });
};
